import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Stack,
} from '@mui/material';
import { Check, Refresh, Email } from '@mui/icons-material';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

interface InlineEmailVerificationProps {
  email: string;
  onVerified: () => void;
  language?: 'en' | 'it';
  autoSendCode?: boolean; // Controls whether to auto-send code on mount
}

const InlineEmailVerification: React.FC<InlineEmailVerificationProps> = ({
  email,
  onVerified,
  language = 'en',
  autoSendCode = true, // Default to true for backward compatibility
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const initialSendRef = useRef(false);

  useEffect(() => {
    // Use ref to ensure this only runs once, even in StrictMode
    if (!initialSendRef.current && autoSendCode) {
      initialSendRef.current = true;
      sendVerificationCode();
    }
  }, [autoSendCode]); // Include autoSendCode in dependencies

  useEffect(() => {
    // Countdown timer for resend button
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const sendVerificationCode = async () => {
    setLoading(true);
    setError('');
    
    try {
      await api.post('/send-verification-code', { 
        email,
        language 
      });
      setCodeSent(true);
      setResendTimer(60); // 60 second cooldown before resending
    } catch (err: any) {
      // Don't show "user already exists" errors - this is expected for existing users
      const errorMessage = err.response?.data?.detail || 'Failed to send verification code';
      if (!errorMessage.toLowerCase().includes('already exists')) {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      await api.post('/verify-code', {
        email,
        code,
      });
      
      setSuccess(true);
      
      // Call parent callback after short delay
      setTimeout(() => {
        onVerified();
      }, 1500);
      
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setVerifying(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setCode(value);
      
      // Auto-verify when 6 digits are entered
      if (value.length === 6) {
        setCode(value);
        setTimeout(() => {
          const button = document.getElementById('verify-button');
          button?.click();
        }, 100);
      }
    }
  };

  if (success) {
    return (
      <Paper sx={{ p: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Check />
          <Typography variant="h6">
            {t('verification.emailVerified')}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email color="primary" />
          <Typography variant="h6">
            {t('verification.verifyEmail')}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {codeSent 
            ? t('verification.codeSentTo', { email })
            : t('verification.sendingCode')}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('verification.enterCode')}
          </Typography>
          <TextField
            fullWidth
            placeholder="000000"
            value={code}
            onChange={handleCodeChange}
            disabled={verifying || success}
            inputProps={{
              maxLength: 6,
              style: {
                fontSize: '24px',
                letterSpacing: '8px',
                textAlign: 'center',
                fontWeight: 'bold',
              },
            }}
            sx={{
              '& input': {
                py: 2,
              },
            }}
          />
        </Box>

        <Stack direction="row" spacing={2}>
          <Button
            id="verify-button"
            variant="contained"
            fullWidth
            onClick={verifyCode}
            disabled={code.length !== 6 || verifying || success}
            startIcon={verifying ? <CircularProgress size={20} /> : <Check />}
          >
            {verifying ? t('verification.verifying') : t('verification.verify')}
          </Button>

          <Button
            variant="outlined"
            fullWidth
            onClick={sendVerificationCode}
            disabled={loading || resendTimer > 0}
            startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
          >
            {resendTimer > 0 
              ? t('verification.resendIn', { seconds: resendTimer })
              : t('verification.resendCode')}
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary" align="center">
          {t('verification.codeExpires')}
        </Typography>
      </Stack>
    </Paper>
  );
};

export default InlineEmailVerification;